/**
 * Micro-helper de creation d'elements. Pas de framework, pas de VDOM :
 * l'interface est petite, un constructeur type suffit et le bundle reste nu.
 */

export type Child = Node | string | number | null | false | undefined;

type EventMap = HTMLElementEventMap;

export interface ElOptions {
  readonly class?: string;
  readonly text?: string;
  readonly attrs?: Readonly<Record<string, string | number | boolean | null>>;
  readonly data?: Readonly<Record<string, string>>;
  readonly style?: Readonly<Partial<Record<string, string>>>;
  readonly on?: {
    readonly [K in keyof EventMap]?: (event: EventMap[K]) => void;
  };
}

/** Cree un element type, avec classes, attributs, data-*, styles et handlers. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: ElOptions = {},
  children: readonly Child[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  if (options.class !== undefined) node.className = options.class;
  if (options.text !== undefined) node.textContent = options.text;

  if (options.attrs) {
    for (const [key, value] of Object.entries(options.attrs)) {
      if (value === null || value === false) continue;
      node.setAttribute(key, value === true ? '' : String(value));
    }
  }

  if (options.data) {
    for (const [key, value] of Object.entries(options.data)) node.dataset[key] = value;
  }

  if (options.style) {
    for (const [key, value] of Object.entries(options.style)) {
      if (value === undefined) continue;
      node.style.setProperty(key, value);
    }
  }

  if (options.on) {
    for (const [type, handler] of Object.entries(options.on)) {
      if (typeof handler !== 'function') continue;
      node.addEventListener(type, handler as unknown as EventListener);
    }
  }

  append(node, children);
  return node;
}

export function append(parent: Node, children: readonly Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }
}

export function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/** Icone SVG inline (aucune requete reseau, aucune police d'icones). */
export function icon(path: string, options: { readonly size?: number } = {}): SVGSVGElement {
  const size = options.size ?? 20;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.classList.add('icon');

  const shape = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  shape.setAttribute('d', path);
  shape.setAttribute('fill', 'none');
  shape.setAttribute('stroke', 'currentColor');
  shape.setAttribute('stroke-width', '1.6');
  shape.setAttribute('stroke-linecap', 'round');
  shape.setAttribute('stroke-linejoin', 'round');
  svg.appendChild(shape);
  return svg;
}

/**
 * Feuille superposee (panneau lateral en paysage, feuille montante en
 * portrait). Primitive partagee par les panneaux Parametres, Equite et par les
 * modales : elle vit ici, dans le module le plus bas de la pile UI, pour
 * qu'aucun panneau n'ait besoin d'importer l'ecran qui l'ouvre.
 */
export interface Sheet {
  readonly root: HTMLElement;
  readonly body: HTMLElement;
  readonly close: () => void;
}

export interface SheetOptions {
  readonly title: string;
  readonly subtitle?: string;
  readonly onClose?: () => void;
  /** `false` pour une modale qu'on ne peut pas balayer (rappel de pause). */
  readonly dismissible?: boolean;
  readonly variant?: 'panel' | 'modal';
}

export function createSheet(options: SheetOptions): Sheet {
  const dismissible = options.dismissible ?? true;
  const variant = options.variant ?? 'panel';
  const previouslyFocused = document.activeElement;

  const body = el('div', { class: 'sheet__body' });

  const closeButton = el(
    'button',
    {
      class: 'sheet__close',
      attrs: { type: 'button', 'aria-label': 'Fermer' },
      on: { click: () => close() },
    },
    [icon(ICONS.close, { size: 18 })],
  );

  const card = el(
    'section',
    {
      class: 'sheet',
      data: { variant },
      attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': options.title },
    },
    [
      el('header', { class: 'sheet__head' }, [
        el('div', { class: 'sheet__titles' }, [
          el('h2', { class: 'sheet__title', text: options.title }),
          options.subtitle !== undefined
            ? el('p', { class: 'sheet__subtitle', text: options.subtitle })
            : null,
        ]),
        dismissible ? closeButton : null,
      ]),
      body,
    ],
  );

  const root = el(
    'div',
    {
      class: 'sheet-scrim',
      data: { variant },
      on: {
        click: (event: MouseEvent) => {
          if (dismissible && event.target === root) close();
        },
      },
    },
    [card],
  );

  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && dismissible) {
      event.preventDefault();
      close();
    }
  };

  let closed = false;
  function close(): void {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    root.dataset['state'] = 'closing';

    let settled = false;
    const done = (): void => {
      if (settled) return;
      settled = true;
      root.remove();
    };
    // Filet de securite : si la transition ne demarre pas (animations reduites,
    // onglet en arriere-plan), on retire quand meme le panneau.
    window.setTimeout(done, 400);
    const animations = root.getAnimations({ subtree: true });
    if (animations.length > 0) {
      Promise.allSettled(animations.map((animation) => animation.finished)).then(done, done);
    }
    if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    options.onClose?.();
  }

  document.addEventListener('keydown', onKey);
  document.body.appendChild(root);
  window.requestAnimationFrame(() => {
    root.dataset['state'] = 'open';
    const focusable = card.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus({ preventScroll: true });
  });

  return { root, body, close };
}

/** Trace d'icones utilisees dans l'interface. */
export const ICONS = {
  settings:
    'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 8.9 19.3a1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.88 1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.7 8.9a1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.88.34H9a1.7 1.7 0 0 0 1-1.56V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.88V9a1.7 1.7 0 0 0 1.56 1H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1Z',
  shield: 'M12 3 4.5 6v5.5c0 4.4 3.1 8.5 7.5 9.5 4.4-1 7.5-5.1 7.5-9.5V6L12 3Z M9.2 12.2l2 2 3.6-3.9',
  gift: 'M4 11h16v9H4z M2 7h20v4H2z M12 7v13 M12 7S10.5 3 8 3a2.5 2.5 0 0 0 0 5h4Zm0 0s1.5-4 4-4a2.5 2.5 0 0 1 0 5h-4Z',
  close: 'M6 6l12 12M18 6 6 18',
  volume: 'M4 9v6h4l5 4V5L8 9H4Z M16.5 8.5a5 5 0 0 1 0 7 M19.5 6a8.5 8.5 0 0 1 0 12',
  muted: 'M4 9v6h4l5 4V5L8 9H4Z M22 9l-5 6M17 9l5 6',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z M12 7v5l3 2',
  back: 'M15 5l-7 7 7 7',
  dice: 'M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z M8.5 8.5h.01M15.5 15.5h.01M12 12h.01',
} as const;
